import { resolveApiBaseUrl } from "../services/apiFetch";
import { isStartableAuthProvider } from "./providerCapabilities";
import { normalizeInternalReturnPath } from "./returnPath";

// The two surfaces a sign-in can begin at. The backend validates this again
// against the same pair; sending it lets a recoverable failure return the user
// to the screen they actually started from.
const AUTH_SURFACES = new Set(["/auth", "/login"]);

// Ordinary local API fetches deliberately keep using CRA's same-origin proxy,
// for which an empty API base is correct. OAuth start is different: it is a
// top-level document navigation, and CRA serves its SPA fallback for that
// relative URL instead of making the backend the browser-visible authority.
// Keep this one trusted development default here, not in screens/components.
export const LOCAL_OAUTH_API_ORIGIN = "http://localhost:5001";
const PROVIDER_AUTHORIZATION_HOSTS = Object.freeze({
  google: "accounts.google.com",
});

// Email is a Lyft Zone screen, not an OAuth redirect.
function isOAuthProvider(providerId) {
  return isStartableAuthProvider(providerId) && providerId !== "email";
}

export function resolveOAuthApiOrigin(
  configuredUrl = process.env.REACT_APP_API_BASE_URL,
  environment = process.env.NODE_ENV
) {
  const configuredBase = resolveApiBaseUrl(configuredUrl);
  const candidate = configuredBase ||
    (environment === "development" || environment === "test"
      ? LOCAL_OAUTH_API_ORIGIN
      : "");

  if (!candidate) {
    throw new Error("REACT_APP_API_BASE_URL is required for OAuth navigation");
  }

  let parsed;
  try {
    parsed = new URL(candidate);
  } catch {
    throw new Error("REACT_APP_API_BASE_URL must be an absolute HTTP(S) origin");
  }

  if (
    (parsed.protocol !== "http:" && parsed.protocol !== "https:") ||
    parsed.username ||
    parsed.password ||
    parsed.pathname !== "/" ||
    parsed.search ||
    parsed.hash
  ) {
    throw new Error("REACT_APP_API_BASE_URL must be an absolute HTTP(S) origin");
  }
  return parsed.origin;
}

export function buildProviderStartUrl({ providerId, returnTo = "/", surface = "/auth" } = {}) {
  if (!isOAuthProvider(providerId)) {
    return null;
  }

  const params = new URLSearchParams({
    returnTo: normalizeInternalReturnPath(returnTo) || "/",
    surface: AUTH_SURFACES.has(surface) ? surface : "/auth",
  });
  const url = new URL(
    `/api/auth/start/${encodeURIComponent(providerId)}`,
    `${resolveOAuthApiOrigin()}/`
  );
  url.search = params.toString();
  return url.toString();
}

// A full-page navigation, deliberately. This is a server OAuth flow: the SPA
// never sees an authorization code, a provider token, or the PKCE verifier, and
// there is no Google client-side SDK involved.
export function startProviderAuthentication(options, navigator = window.location) {
  const url = buildProviderStartUrl(options);
  if (!url) {
    return false;
  }
  navigator.assign(url);
  return true;
}

export function navigateToProviderAuthorization(
  { providerId, authorizationUrl },
  navigator = window.location
) {
  const expectedHost = PROVIDER_AUTHORIZATION_HOSTS[providerId];
  if (!expectedHost) {
    throw new Error("Unsupported OAuth provider authorization URL");
  }

  let parsed;
  try {
    parsed = new URL(authorizationUrl);
  } catch {
    throw new Error("Invalid OAuth provider authorization URL");
  }
  if (
    parsed.protocol !== "https:" ||
    parsed.hostname !== expectedHost ||
    parsed.username ||
    parsed.password
  ) {
    throw new Error("Invalid OAuth provider authorization URL");
  }
  navigator.assign(parsed.toString());
  return true;
}
